import { get } from "svelte/store";
import { cueConversationAndPlay } from "@components/ConversationVideo";
import { shuffleVideos } from "@components/Video";
import { cueConversationVideo } from "@lib/client/video";
import {
  NLURequestType,
  logNLUFailure,
  logNLUSuccess,
  logVideoComplete,
  logVideoStart
} from "@services/analytics";
import { _ } from "@services/i18n";
import { authenticatedFetch } from "@stores/auth";
import { featureEnabled } from "@stores/features";
import { displayAllowedQuestions } from "@stores/features/allowed-questions";
import { tests } from "@stores/features/hide-controls";
import { resetLinks } from "@stores/features/links";
// eslint-disable-next-line max-len
import {
  displaySuggestedQuestions,
  suggestedQuestionsSheetOpen
} from "@stores/features/suggested-questions";
import * as stores from "@stores/global";
import { notify } from "@stores/notification";
import { showPaywall } from "@stores/sheets";
import { conversation } from "@stores/videos";
import getVideoSource from "@utils/get-video-source";
import { logTiming } from "./analytics";

/**
 * Process errors in nlu response
 *
 * @param {Response} res fetch response
 * @returns {Promise} rejected promise
 */
function handleError(res) {
  return new Promise((_resolve, reject) => {
    res
      .json()
      .then((body) => {
        // eslint-disable-next-line no-console
        console.warn("An error has occurred in the worker", body);
        reject(body);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.warn(
          "An error has occurred decoding the error response from the worker",
          error
        );
        reject(error);
      });
  });
}

/**
 * Submit either an audio clip or a string of text
 * representing a question to nlu
 *
 * @param {object} options
 * @param {string} options.payload text or base64 audio clip
 * @param {string} options.experienceId
 * @param {string} options.type type of request, either "audio" or "text"
 * @returns {Promise}
 */
export function submit({ payload, experienceId, type }) {
  if (!["audio", "text"].includes(type)) {
    throw new Error("Type must be either `text` or `audio`");
  }
  if (!payload) {
    throw new Error("Payload is expected");
  }
  if (!experienceId || typeof experienceId !== "string") {
    throw new Error("Experience id must be a non-empty string");
  }

  const requested = Date.now();

  return authenticatedFetch("/speak", {
    body: JSON.stringify({
      experienceId: experienceId,
      type,
      payload,
      contexts: (get(stores.response) || {}).contexts || []
    }),
    headers: { "Content-Type": "application/json; charset=utf-8" },
    method: "POST"
  })
    .then((data) => {
      logTiming(
        "nlu_agent_request",
        type,
        Date.now() - requested,
        `Send ${type} to worker and get response`
      );

      stores.response.set(data);
      return data;
    })
    .catch((res) => {
      logTiming(
        "nlu_agent_request",
        type,
        Date.now() - requested,
        `Send ${type} to worker and get response`
      );
      return handleError(res);
    });
}

// eslint-disable-next-line no-empty-function
const noop = () => {};

/**
 * Submit query to nlu and match intents.
 * Call optional callbacks at various stages, and
 * cue incoming video response.
 *
 * @param {object} options
 * @param {string} options.experienceId Experience ID
 * @param {any} options.payload Request payload
 * @param {string} options.type Type of request (audio, event, text)
 * @param {Function} options.onAfterSubmit post-submission callback
 * @param {Function} options.onBeforeSubmit pre-submission callback
 * @param {Function} options.onError error callback
 * @param {Function} options.onVideoEnd callback once video has played through
 * @param {Function} options.onVideoPlayback callback once video starts playing
 * @returns {Promise}
 */
// eslint-disable-next-line max-lines-per-function
export async function getIntent({
  experienceId,
  payload,
  type,
  onAfterSubmit = noop,
  onBeforeSubmit = noop,
  onError = noop,
  onVideoEnd = noop,
  onVideoPlayback = noop
} = {}) {
  try {
    stores.displayTooltip.update(() => false);
    if (featureEnabled("links")) {
      resetLinks();
    }
    onBeforeSubmit();
    const data = await submit({ experienceId, payload, type });
    const { intent } = data.payload;
    logNLUSuccess(intent, NLURequestType[type]);
    if (featureEnabled("allowed-questions")) {
      displayAllowedQuestions.update(() => false);
    }
    if (featureEnabled("suggested-questions")) {
      displaySuggestedQuestions.update(() => false);
      suggestedQuestionsSheetOpen.update(() => false);
    }
    onAfterSubmit();
    cueConversationAndPlay({
      onEnd: onVideoEnd,
      onPlayback: onVideoPlayback,
      src: get(conversation),
      title: data.payload.asset || data.payload.intent,
      video: get(stores.conversationVideo)
    });
  } catch (err) {
    logNLUFailure(
      // Don't log audio requests, it's too much data
      (type === "audio" ? type : payload) || "Empty payload!",
      NLURequestType[type]
    );
    onError();
    const fallbacks = get(stores.experience).media.fallbacks;
    if (typeof fallbacks === "object" && Object.keys(fallbacks).length) {
      cueConversationAndPlay({
        src: getVideoSource({
          experience: get(stores.experience),
          video: shuffleVideos(fallbacks)
        }),
        title: "fallback",
        video: get(stores.conversationVideo)
      });
    } else {
      // eslint-disable-next-line no-underscore-dangle
      const $_ = get(_);
      notify(
        $_("errors.generic", {
          default: "Something went wrong, please try again"
        })
      );
    }
    // eslint-disable-next-line no-console
    console.error(err);
  }
}

/**
 * Get a start intent (contextual intro) for
 * a given experience
 *
 * @param {string} experienceId
 * @param {boolean} shouldPlay If video should autoplay
 * @returns {Promise}
 */
// eslint-disable-next-line max-lines-per-function
export function getStartIntent(experienceId, shouldPlay = true) {
  if (!experienceId || typeof experienceId !== "string") {
    throw new Error("Experience id must be a non-empty string");
  }

  const requested = Date.now();

  return authenticatedFetch(`/experience/${experienceId}/start-intent`, {
    headers: { "Content-Type": "application/json; charset=utf-8" },
    method: "GET"
  })
    .then((data) => {
      logTiming(
        "nlu_agent_request",
        "start_intent",
        Date.now() - requested,
        "Get start intent response from worker"
      );

      stores.response.set(data);
      return data;
    })
    .then((data) => {
      if (shouldPlay) {
        cueConversationAndPlay({
          onEnd: () => {
            stores.introSeen.update(() => true);
            showPaywall.update(() => false);
          },
          src: get(conversation),
          title: data.payload.asset || data.payload.intent,
          video: get(stores.conversationVideo)
        });
      } else {
        const src = get(conversation);
        const title = data.payload.asset || data.payload.intent;
        const video = get(stores.conversationVideo);
        cueConversationVideo(video, src, function beforePlaying() {
          let ended = false;

          tests.update((old) => ({ ...old, videoend: false }));

          video.onended = function conversationVideoOnEnded() {
            if (!ended) {
              ended = true;
              tests.update((old) => ({ ...old, videoend: true }));
              logVideoComplete(title, video.duration);
            }
          };

          video.onloadedmetadata = function conversationVideoOnLoadMetadata() {
            logVideoStart(title, video.duration);
          };

          /**
           * If video has ended, trigger ended event
           * This is due to iOS occassionally not firing
           * ended event
           */
          function conversationVideoOnTimeUpdate() {
            if (!ended && this.duration - this.currentTime < 0.2) {
              this.dispatchEvent(new Event("ended"));
              video.removeEventListener(
                "timeupdate",
                conversationVideoOnTimeUpdate
              );
            }
          }

          video.addEventListener("timeupdate", conversationVideoOnTimeUpdate);
        });
      }
      return data;
    })
    .catch((res) => {
      logTiming(
        "nlu_agent_request",
        "start_intent",
        Date.now() - requested,
        "Get start intent response from worker"
      );
      return handleError(res);
    });
}
